import type { stacks } from "@dropbox/api-v2-client";
import { ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { useCurrentBackgroundTheme } from "@mirage/stacks/themes";
import { canEditStack, getStackBannerUrl } from "@mirage/stacks/utils";
import classNames from "classnames";
import { useState } from "react";
import { useShareModal } from "../ShareModal/ShareModal";
import styles from "./Header.module.css";
import { HeaderActions } from "./HeaderActions";
import { ShareStackButton } from "./ShareStackButton";
import { StackDescriptionEditor } from "./StackDescriptionEditor";
import { StackIconEditor } from "./StackIconEditor";
import { StackMembersFacepile } from "./StackMembersFacepile";
import { StackNameEditor } from "./StackNameEditor";
import { StackSettingsMenu } from "./StackSettingsMenu";

export const Header = ({
  initialCanEdit,
  mutationRequestId,
  sessionId,
  stack,
}: {
  initialCanEdit: boolean;
  mutationRequestId?: string;
  sessionId?: string;
  stack: stacks.Stack | null;
}) => {
  const backgroundTheme = useCurrentBackgroundTheme(stack);
  const { shareModalJsx, openShareModal } = useShareModal({ stack });
  const isMobileSize = useIsMobileSize();

  const [isEditingStackName, setIsEditingStackName] = useState(false);

  const isNewStack = stack === null;
  const canEdit = initialCanEdit ?? canEditStack(stack);
  const bannerUrl = getStackBannerUrl(stack);
  const cssProperties = {
    "--stack-banner-url": bannerUrl ? `url(${bannerUrl})` : "none",
  } as React.CSSProperties;

  return (
    <ThemeProvider overrides={backgroundTheme}>
      <ThemeContainer>
        <div
          className={classNames(
            styles.headerContainer,
            bannerUrl && styles.headerContainerWithBanner,
          )}
          // set variables for the background image
          style={cssProperties}
        >
          <StackIconEditor
            canEdit={canEdit}
            stack={stack}
            mutationRequestId={mutationRequestId}
            sessionId={sessionId}
          />
          <div className={styles.headerMainRow}>
            <div>
              <StackNameEditor
                canEdit={canEdit}
                editing={isEditingStackName}
                mutationRequestId={mutationRequestId}
                onEditStatusUpdate={setIsEditingStackName}
                sessionId={sessionId}
                stack={stack}
              />
              <StackDescriptionEditor
                canEdit={canEdit}
                editing={isEditingStackName}
                mutationRequestId={mutationRequestId}
                onEditStatusUpdate={setIsEditingStackName}
                sessionId={sessionId}
                stack={stack}
              />
            </div>
            <div className={styles.headerRightSection}>
              {stack && (
                <StackMembersFacepile onClick={openShareModal} stack={stack} />
              )}
              <ShareStackButton
                disabled={isNewStack}
                iconType="arrow"
                stack={stack}
                onClick={openShareModal}
              />
              <StackSettingsMenu
                mutationRequestId={mutationRequestId}
                stack={stack}
              />
            </div>
          </div>
          <div
            className={classNames(styles.headerActionsContainer, {
              [styles.headerActionsContainerMobile]: isMobileSize,
            })}
          >
            <div className={styles.headerActionsItemContainer}>
              <HeaderActions isNewStack={isNewStack} stack={stack} />
            </div>
          </div>
        </div>
        {shareModalJsx}
      </ThemeContainer>
    </ThemeProvider>
  );
};
